import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import {
  Button,
  Card,
  Dialog,
  DropdownInput,
  EmptyState,
  Form,
  Icon,
  RadioInput,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import { FormSubmissionTaskType } from "@validereinc/domain";
import { toStartCaseString } from "@validereinc/utilities";
import { produce } from "immer";
import React, { useEffect, useMemo, useState } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";

const fieldsSupportedAsFormSubmissionOutput = ["id"];

type FormValuesType = {
  outputType: "primaryFields" | "answers";
  answers?: {
    sectionId: string;
    sectionIndex: string;
    questionId: string;
  };
  primaryField?: string;
  variableName: string;
};

const VariableCard = ({
  variableName,
  variableValue,
  formSchemaId,
  removeVariable,
}: {
  variableName: string;
  variableValue: string;
  formSchemaId: string;
  removeVariable: (variableName: string) => void;
}) => {
  const formSchemaQuery = useGetOneFormSchema(
    {
      id: formSchemaId,
    },
    { enabled: !!formSchemaId }
  );

  let value = variableValue;
  if (variableValue.startsWith("$.answers")) {
    const regex = /\.answers\.([^.[]+)\[([^\]]+)\]\.([^.]+)\./;
    const match = variableValue.match(regex);
    if (!match) return null;
    const [_, sectionId, sectionIndex, questionId] = match;
    const section = formSchemaQuery.data?.data.config.sections.find(
      (section) => section.id === sectionId
    );

    const sectionName = section?.name ?? "";

    const questionPrompt =
      formSchemaQuery.data?.data?.config.questions?.[questionId]?.prompt ?? "";

    const values: string[] = [];

    values.push(`Section Name: ${sectionName}`);

    if (section?.is_repeatable) values.push(`Section Index: ${sectionIndex}`);

    values.push(`Question: ${questionPrompt}`);
    value = values.join(", ");
  } else if (
    variableValue.startsWith("$.") &&
    fieldsSupportedAsFormSubmissionOutput.includes(
      variableValue.replace("$.", "")
    )
  ) {
    value = `Form Submission ${toStartCaseString(variableValue.replace("$.", ""))}`;
  }
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <b style={{ fontFamily: "monospace" }}>{variableName}</b> is mapped to{" "}
          <br />
          <b>{value}</b>
        </div>
        <Button
          icon="minus-circle"
          variant="error-outline"
          style={{ width: "fit-content" }}
          onClick={() => {
            removeVariable(variableName);
          }}
        />
      </div>
    </Card>
  );
};

const WorkflowConfigurationOutputVariableForm = ({
  stepDefinition,
  selectedNodeId,
}: {
  stepDefinition: FormSubmissionTaskType;
  selectedNodeId?: string;
}) => {
  const formSchemaQuery = useGetOneFormSchema(
    {
      id: stepDefinition.form_schema_id,
    },
    { enabled: !!stepDefinition.form_schema_id }
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [localVariables, setLocalVariables] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    setLocalVariables(stepDefinition.output ?? {});
  }, [JSON.stringify(stepDefinition.output)]);

  const form = useForm<FormValuesType>({
    defaultValues: {
      answers: { sectionIndex: "0" },
      variableName: "",
    },
  });

  const [
    outputType,
    selectedSectionId,
    selectedQuestionId,
    selectedPrimaryField,
  ] = form.watch([
    "outputType",
    "answers.sectionId",
    "answers.questionId",
    "primaryField",
  ]);

  const formSchema = formSchemaQuery.data?.data;

  const sectionsList =
    formSchema?.config.sections.map((section) => ({
      label: section.name,
      value: section.id,
    })) ?? [];

  const questionsList = (
    formSchema?.config.sections.find(
      (section) => section.id === selectedSectionId
    )?.questions ?? []
  ).map((value) => ({
    label: formSchema?.config?.questions?.[value]?.prompt ?? "",
    value,
  }));

  const fieldsList = fieldsSupportedAsFormSubmissionOutput.map((value) => ({
    label: `Form Submission "${value}"`,
    value,
  }));

  const isSelectedSectionRepeatable = useMemo(() => {
    const sectionDefinition = formSchema?.config.sections.find(
      (s) => s.id === selectedSectionId
    );

    if (!sectionDefinition) return false;

    return sectionDefinition.is_repeatable;
  }, [formSchema, selectedSectionId]);

  const { setOutput } = useWorkflowConfigurationFormStore();

  useEffect(() => {
    if (isDialogOpen) {
      form.reset({ answers: { sectionIndex: "0" } });
      setOutput(localVariables);
    }
  }, [isDialogOpen, JSON.stringify(localVariables)]);

  const handleSubmit = (data: FormValuesType) => {
    if (!data.outputType || !selectedNodeId) return;

    if (data.outputType === "primaryFields" && data.primaryField) {
      setLocalVariables((currentValue) =>
        produce(currentValue, (draft) => {
          draft[data.variableName] = `$.${data.primaryField}`;
        })
      );
    }

    setLocalVariables((currentValue) =>
      produce(currentValue, (draft) => {
        if (
          data.outputType === "answers" &&
          data.answers?.questionId &&
          data.answers?.sectionId &&
          data.answers?.sectionIndex
        )
          draft[data.variableName] =
            `$.answers.${data.answers.sectionId}[${data.answers.sectionIndex}].${data.answers.questionId}.value`;
      })
    );

    setIsDialogOpen(false);
  };

  const shouldRenderVariablePicker =
    (outputType === "primaryFields" && !!selectedPrimaryField) ||
    (outputType === "answers" && !!selectedQuestionId);

  useEffect(() => {
    form.setValue("answers.questionId", "");
  }, [selectedSectionId]);

  const variables = Object.entries(localVariables);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {variables.length === 0 && (
          <EmptyState
            title="No output variables created"
            suggestion="Create output variables from selected form questions and answers to re-use in the workflow template."
            icon={<Icon variant="files" />}
            style={{ padding: 16 }}
          />
        )}

        {variables.map((variable) => (
          <VariableCard
            key={variable[0]}
            variableName={variable[0]}
            variableValue={variable[1]}
            formSchemaId={stepDefinition.form_schema_id}
            removeVariable={(variableName) => {
              setLocalVariables((currentValue) =>
                produce(currentValue, (draft) => {
                  delete draft[variableName];
                })
              );
            }}
          />
        ))}

        <Button
          icon="plus-circle"
          style={{ width: "100%" }}
          onClick={() => setIsDialogOpen(true)}
        >
          Create Output Variables
        </Button>
      </div>

      <Dialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        title="Create Output Variable"
        actionRow={[
          <Button
            key="create"
            variant="primary"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={form.handleSubmit(handleSubmit)}
            disabled={!form.formState.isValid || !outputType}
          >
            Create
          </Button>,
        ]}
      >
        <Form {...form}>
          <RadioInput
            isRequired
            label="Data Source"
            name="outputType"
            options={[
              { label: "Form Submission Metadata", value: "primaryFields" },
              { label: "Form Submission Answers", value: "answers" },
            ]}
            labelKey="label"
            valueKey="value"
          />

          {outputType === "primaryFields" && (
            <DropdownInput
              isRequired
              noSearchDebounce
              name="primaryField"
              options={fieldsList}
              labelKey="label"
              valueKey="value"
              label="Metadata field"
            />
          )}
          {outputType === "answers" && (
            <>
              <DropdownInput
                isRequired
                noSearchDebounce
                name="answers.sectionId"
                options={sectionsList}
                labelKey="label"
                valueKey="value"
                label="Section"
                isClearable={false}
              />
              {isSelectedSectionRepeatable && (
                <TextInput
                  isRequired
                  name="answers.sectionIndex"
                  label="Section Index (number / variable)"
                />
              )}
              {selectedSectionId && (
                <DropdownInput
                  isRequired
                  noSearchDebounce
                  name="answers.questionId"
                  options={questionsList}
                  labelKey="label"
                  valueKey="value"
                  label="Question"
                  isClearable={false}
                />
              )}
            </>
          )}

          {shouldRenderVariablePicker && (
            <TextInput
              label="Variable Name"
              name="variableName"
              isRequired
              rules={{
                validate: {
                  validateVariable: (value: string) =>
                    value && /^\$[A-Z][A-Z0-9_]*$/.test(value)
                      ? true
                      : "Incorrect Variable Format",
                  validateExistingVariable: (value: string) =>
                    Object.keys(stepDefinition.output ?? {}).includes(value)
                      ? "This variable already exists for this step."
                      : true,
                },
              }}
            />
          )}
        </Form>
      </Dialog>
    </>
  );
};

export default WorkflowConfigurationOutputVariableForm;
